import React from 'react';

import LoadingSpinnerLong from '../../../molecules/LoadingSpinnerLong/LoadingSpinnerLong.react';
import EmptyTable from '../EmptyTable/EmptyTable.react';

import DataTableRow from './DataTableRow';
import type {DataTableBodyProps} from './DataTable.types';

/**
 * Renders the `<tbody>` for DataTable.
 *
 * Handles three states:
 * 1. **Loading** – full-table spinner (initial fetch)
 * 2. **Reloading** – semi-transparent overlay spinner (rendered by DataTable above the table;
 *    tbody still shows the previous rows so the layout doesn't shift).
 * 3. **Data / Empty** – rows or empty-state fallback
 *
 * Uses the existing `data-table__*` CSS classes for visual parity.
 */
function DataTableBody<TData>({
  table,
  isLoading,
  emptyState,
  columnCount
}: DataTableBodyProps<TData>) {
  if (isLoading) {
    return (
      <tbody>
        <tr>
          <td className='data-table__loading' colSpan={columnCount}>
            <LoadingSpinnerLong />
          </td>
        </tr>
      </tbody>
    );
  }

  const rows = table.getRowModel().rows;
  const isEmpty = rows.length === 0;
  const emptyContent = emptyState ?? <EmptyTable />;

  return (
    <tbody>
      {isEmpty ? emptyContent : rows.map((row) => <DataTableRow key={row.id} row={row} />)}
    </tbody>
  );
}

export default DataTableBody;
